import React from 'react';
import { Document, View, Text, StyleSheet } from '@react-pdf/renderer';
import PageA4 from 'PDF/Elements/Pages/PageA4';
import LegacyHeaderSection from 'PDF/TemplateEngine/sections/LegacyHeaderSection';
import LegacyFooterSection from 'PDF/TemplateEngine/sections/LegacyFooterSection';
import HeaderSection from 'PDF/TemplateEngine/sections/HeaderSection';
import FooterSection from 'PDF/TemplateEngine/sections/FooterSection';
import PortraitSection from 'PDF/TemplateEngine/sections/PortraitSection';
import SectionRenderer from 'PDF/TemplateEngine/SectionRenderer';
import {
  TemplateColorsProvider,
  useTemplateColors,
} from 'PDF/TemplateEngine/TemplateColorsContext';
import { useTenantColors } from 'hooks/useTenantColors';

// Separa el array sections en (portada, header, footer, body) para que el
// TemplateRenderer pueda posicionar cada tipo donde corresponde. Solo la
// PRIMERA section de cada type posicional se considera — si el usuario agrega
// más de una header/footer, las siguientes se ignoran (limitación conocida,
// el editor debería impedirlo en UI).
function splitSections(sections) {
  const list = Array.isArray(sections) ? sections : [];
  const portraits = [];
  let header = null;
  let footer = null;
  const body = [];
  for (const s of list) {
    if (!s || s.visible === false) continue;
    if (s.type === 'portrait') portraits.push(s);
    else if (s.type === 'header' && !header) header = s;
    else if (s.type === 'footer' && !footer) footer = s;
    else body.push(s);
  }
  return { portraits, header, footer, body };
}

// Componente interno que vive DENTRO del provider, por lo que puede usar
// useTemplateColors para acceder a baseStyles ya memoizado con los colores
// del tenant.
// Banda de disclaimer fija al pie (cada página). Cuando se provee
// `footerDisclaimer` REEMPLAZA al footer normal — evita solaparse con el
// FooterSection/LegacyFooterSection que también son `fixed`. Pensado para el
// Reporte de Visita (disclaimer Res. 38 + línea de hash de integridad).
function FooterDisclaimerBand({ footerDisclaimer }) {
  const { colors, FONT } = useTemplateColors();
  const styles = StyleSheet.create({
    band: {
      position: 'absolute',
      bottom: '0.6cm',
      left: '1.5cm',
      right: '1.5cm',
      borderTop: `1px solid ${colors.border}`,
      paddingTop: '0.15cm',
    },
    text: { fontSize: 7, fontFamily: FONT.base, color: colors.muted, textAlign: 'justify' },
    hash: { fontSize: 7, fontFamily: FONT.base, color: colors.muted, marginTop: '0.05cm' },
  });
  return (
    <View style={styles.band} fixed>
      <Text style={styles.text}>{footerDisclaimer.text}</Text>
      {footerDisclaimer.hashLine ? (
        <Text style={styles.hash}>{footerDisclaimer.hashLine}</Text>
      ) : null}
    </View>
  );
}

function TemplateDocument({ template, data, config, footerDisclaimer }) {
  const { baseStyles } = useTemplateColors();
  const { portraits, header, footer, body } = splitSections(template.sections);

  // `template.logo` es el logo global configurado desde el editor low-code
  // (card "Metadatos" del TemplateEditor). Se inyecta como default en
  // config.logo y config.portraitLogo para que HeaderSection / PortraitSection
  // lo consuman sin que cada sección tenga que declararlo en su `config`.
  // El `config` prop explícito (si viene del caller) pisa este default.
  const effectiveConfig = {
    ...(config || {}),
    logo: config?.logo ?? template.logo ?? null,
    portraitLogo: config?.portraitLogo ?? template.logo ?? null,
  };

  return (
    <Document>
      {portraits.map((p) => (
        <PortraitSection key={p.id} section={p} data={data} config={effectiveConfig} />
      ))}
      <PageA4 style={baseStyles.page}>
        {header ? (
          <HeaderSection section={header} data={data} config={effectiveConfig} />
        ) : (
          <LegacyHeaderSection template={template} config={effectiveConfig} />
        )}
        <View style={baseStyles.body}>
          {body.map((section) => (
            <SectionRenderer
              key={section.id}
              section={section}
              data={data}
              config={effectiveConfig}
            />
          ))}
        </View>
        {footerDisclaimer ? (
          <FooterDisclaimerBand footerDisclaimer={footerDisclaimer} />
        ) : footer ? (
          <FooterSection section={footer} data={data} config={effectiveConfig} />
        ) : (
          <LegacyFooterSection config={effectiveConfig} />
        )}
      </PageA4>
    </Document>
  );
}

// `colors` prop opcional — override manual. Si no viene, se consume del
// tenant via useTenantColors().
function TemplateRenderer({ template, data, config, colors, footerDisclaimer }) {
  const tenantColors = useTenantColors();
  const effective = colors ?? tenantColors;

  if (!template) return <Document />;

  return (
    <TemplateColorsProvider colors={effective}>
      <TemplateDocument
        template={template}
        data={data}
        config={config}
        footerDisclaimer={footerDisclaimer}
      />
    </TemplateColorsProvider>
  );
}

export default TemplateRenderer;
